
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import ArticleComponent from "./ArticleComponent.vue";
import AssemblyComponent from "./AssemblyComponent.vue";
import { debounce } from "typescript-debounce-decorator";
import { ArticleDto } from "@/dtos/articleDto";

@Component({
	components: {
		ArticleComponent,
		AssemblyComponent,
	},
})
export default class ArticleDashboard extends Vue {
	@Prop()
	private customerIdProp!: any;
	@Prop()
	private articleIdProp!: any;
	private loading: boolean = false;
	private loadedArticleUpTo = 0;
	private searchterm: string = "";
	private customerId: number = 0;
	private customerIdString: string = "";
	private onlyLocked: boolean = false;
	private includeDeleted: boolean = false;
	private hasMoreToLoad: boolean = true;
	private loadBatchSize: number = 10;
	private filterModifiers: string[] = ["Nur gültige", "Nur gesperrte", "Alle anzeigen"];
	private filterModifier: string = "Nur gültige";
	private cancelSearchtermReload: boolean = false;
	private cancelCustomerIdReload: boolean = false;
	private filters: { searchterm: string; customerId: string; modifier: string } = {
		customerId: "",
		modifier: "Nur gültige",
		searchterm: "",
	};

	private async mounted() {
		let filtersAlreadyLoaded = await this.setProps();
		if (!filtersAlreadyLoaded) {
			const storedFilters = localStorage.getItem("articleFilters");
			if (storedFilters) {
				this.filters = JSON.parse(storedFilters);
				if (this.filters.searchterm) {
					this.cancelSearchtermReload = true;
					this.searchterm = this.filters.searchterm;
				}
				if (this.filters.customerId) {
					this.cancelCustomerIdReload = true;
					this.customerIdString = this.filters.customerId;
					this.setCustomerId();
				}
				if (this.filters.modifier) {
					this.filterModifier = this.filters.modifier;
					this.setModifiers();
				}
			}
		}
		if (this.$store.state.reloadArticles) {
			this.articles = [];
			await this.loadArticle();
		} else {
			if (this.$store.state.articleScrollPosition > 0) {
				window.scrollTo({ top: this.$store.state.articleScrollPosition });
				this.$store.commit("setArticleScrollPosition", 0);
			}
			this.$nextTick(() => {
				this.$store.commit("setReloadArticles", true);
			});
		}
	}

	private beforeMount() {
		window.addEventListener("scroll", this.handleScroll);
	}
	private beforeDestroy() {
		window.removeEventListener("scroll", this.handleScroll);
	}

	// This component can receive optional props when routed with parameters.
	// If they exist, they are loaded and the filters set accordingly.
	// If there are no props given, the filters are loaded from local storage.
	private async setProps(): Promise<boolean> {
		let filtersLoaded = false;
		if (this.customerIdProp !== undefined && !isNaN(parseInt(this.customerIdProp))) {
			this.customerId = this.customerIdProp;
			this.cancelCustomerIdReload = true;
			this.customerIdString = this.customerId.toString();
			filtersLoaded = true;
		}
		if (this.articleIdProp !== undefined && !isNaN(parseInt(this.articleIdProp))) {
			this.cancelSearchtermReload = true;
			this.searchterm = this.articleIdProp;
			filtersLoaded = true;
		}
		if (filtersLoaded) {
			// reset stored filters if loaded with parameters
			localStorage.removeItem("articleFilters");
		}
		return filtersLoaded;
	}

	@debounce(700, { leading: false })
	@Watch("searchterm")
	private reloadOnSearchterm() {
		if (this.cancelSearchtermReload) {
			this.cancelSearchtermReload = false;
			return;
		}
		if (!this.loading && this.$store.state.reloadArticles) {
			this.storeFilters();
			this.articles = [];
			this.loadedArticleUpTo = 0;
			this.loadArticle();
		}
	}

	@debounce(700, { leading: false })
	@Watch("customerIdString")
	private reloadOnCustomerId() {
		if (this.cancelCustomerIdReload) {
			this.cancelCustomerIdReload = false;
			return;
		}
		if (!this.loading && this.$store.state.reloadArticles) {
			this.storeFilters();
			this.setCustomerId();
			this.articles = [];
			this.loadedArticleUpTo = 0;
			this.loadArticle();
		}
	}

	@Watch("filterModifier")
	private reloadOnSpecialFilterChanged() {
		this.setModifiers();
		if (!this.loading && this.$store.state.reloadArticles) {
			this.storeFilters();
			this.articles = [];
			this.loadedArticleUpTo = 0;
			this.loadArticle();
		}
	}

	private setModifiers() {
		switch (this.filterModifier) {
			case "Nur gültige":
				this.onlyLocked = false;
				this.includeDeleted = false;
				break;
			case "Nur gesperrte":
				this.onlyLocked = true;
				this.includeDeleted = false;
				break;
			case "Alle anzeigen":
				this.onlyLocked = false;
				this.includeDeleted = true;
				break;
		}
	}

	private setCustomerId() {
		let parsedId = parseInt(this.customerIdString);
		if (!isNaN(parsedId)) {
			this.customerId = parsedId; // Wenn es eine gültige Zahl ist, verwende sie als Kundennummer
		} else {
			this.customerId = 0;
		}
	}

	private get articles() {
		return this.$store.state.articles.filter((a: ArticleDto) => a.parentArticleId == null);
	}
	private set articles(articles: ArticleDto[]) {
		this.$store.commit("setArticles", articles);
	}
	private removeArticle(articleId: any) {
		// Artikel wurde bereits im Backend gelöscht.
		// Falls vorhanden wird er lokal auch entfernt.
		this.$store.commit("removeArticle", articleId);
	}
	private async loadArticle() {
		try {
			this.loading = true;
			let request =
				`Article/GetArticles?` +
				`searchTerm=${this.searchterm ? this.searchterm : ""}&` +
				`customerId=${this.customerId}&` +
				`from=${this.loadedArticleUpTo}&` +
				`to=${this.loadedArticleUpTo + this.loadBatchSize}&` +
				`onlyLocked=${this.onlyLocked}&` +
				`includeDeleted=${this.includeDeleted}&` +
				`sorting=3`;
			const response = await httpClient().get(request);
			const calculatingArticleIds = response.data
				.filter((a: any) => a.calculating)
				.map((a: any) => a.articleId);
			this.hasMoreToLoad = response.data.length == this.loadBatchSize;
			this.$store.commit("addOrUpdateArticles", response.data);
			this.loadedArticleUpTo += response.data.length;
			this.$store.dispatch("updateValidationProgress", calculatingArticleIds);
		} catch {
			console.log("Failed to load articles.");
		} finally {
			this.loading = false;
		}
	}

	private async handleScroll(event: any) {
		window.removeEventListener("scroll", this.handleScroll);
		const bottomOfWindow =
			window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 2;
		if (bottomOfWindow && !this.loading && this.hasMoreToLoad) {
			this.loadArticle();
		}
		window.addEventListener("scroll", this.handleScroll);
	}

	private storeFilters() {
		localStorage.setItem(
			"articleFilters",
			JSON.stringify({
				searchterm: this.searchterm,
				customerId: this.customerIdString,
				modifier: this.filterModifier,
			})
		);
	}

	private resetFilters() {
		localStorage.removeItem("articleFilters");
		this.searchterm = "";
		this.customerIdString = "";
		this.filterModifier = "Nur gültige";
	}
}
